import { combineLatest, of } from "rxjs";
import { distinctUntilChanged, map, pluck } from "rxjs/operators";
import update from "immutability-helper";

// for finding "{key}"
const regex = new RegExp("{([^{]+)}", "g");

/**
 * Creates value stream by path
 * @param {*} state$
 * @param {*} path
 */
export const getValue$ = (state$, path) => {
  if (typeof path === "string") {
    path = path.split(".");
  }
  return state$.pipe(pluck(...path), distinctUntilChanged());
};

/**
 * Creates property stream from based on template string
 * eg. "name-{path}" --> "name-<value>"
 * @param {*} value
 */
export const property$ = (state$, value) => {
  // find out stream ids
  const found = value.match(regex);

  // get streams
  const var$ = found.map((item) => {
    const var$Name = item.substring(1, item.length - 1);
    return getValue$(state$, var$Name).pipe(map((v) => [item, v]));
  });

  return combineLatest(...var$).pipe(
    map((patches) => {
      // copy orginal value
      let result = value;

      // return variable in orginal format if template contains only variable definition
      // eg. "{path}"
      if (patches.length === 1 && value.length === patches[0][0].length) {
        return patches[0][1];
      }

      // apply patches
      for (const patch of patches) {
        result = result.replace(...patch);
      }
      return result;
    })
  );
};

/**
 * Finds template strings used in object and
 * returns paths and value streams
 *
 * @param {*} state$
 * @param {*} object
 * @param {*} path
 */
export const properties$ = (state$, object, path = []) => {
  let result = [];

  // find props with themplate strings
  for (const key in object) {
    if (object.hasOwnProperty(key)) {
      const value = object[key];
      if (typeof value === "string" && regex.test(value)) {
        result.push([[...path, key], property$(state$, value)]);
      } else if (typeof value === "object" && !Array.isArray(value)) {
        result = [...result, ...properties$(state$, value, [...path, key])];
      }
    }
  }

  return result;
};

/**
 * Creates update object
 * see: https://reactjs.org/docs/update.html
 */
const createUpdate = (paths, values) => {
  const update = {};

  for (let index = 0; index < paths.length; index++) {
    const path = paths[index];
    const value = values[index];

    let obj = update;

    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      if (!obj.hasOwnProperty(key)) {
        obj[key] = {};
      }
      obj = obj[key];
    }

    const lastKey = path[path.length - 1];

    obj[lastKey] = { $set: value };
  }

  return update;
};

/**
 * Creates object stream.
 *
 * @param {*} state$
 * @param {*} obj
 */
export const object$ = (state$, obj) => {
  // get property streams [[...path], property$]
  const props = properties$(state$, obj);
  const paths = props.map((item) => item[0]);

  if (props.length === 0) {
    return of(obj);
  }

  return combineLatest(...props.map((prop) => prop[1])).pipe(
    map((values) => {
      return update(obj, createUpdate(paths, values));
    })
  );
};
